import React, { useState } from 'react';
import { X } from 'lucide-react';
import { withdrawPoints } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import Button from './Button';
import Input from './Input';
import './PointBankModal.css';

function PointBankModal({ player, onClose, onPointsUpdated }) {
  const { user } = useAuth();
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [pin, setPin] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleWithdrawal = async (e) => {
    e.preventDefault();

    if (!withdrawalAmount || !pin) {
      setError('Please enter both amount and PIN');
      return;
    }

    const amount = parseInt(withdrawalAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amount > player.totalScore) {
      setError('Insufficient points');
      return;
    }

    if (pin.length !== 4) {
      setError('PIN must be 4 digits');
      return;
    }

    setIsWithdrawing(true);
    setError('');

    try {
      const result = await withdrawPoints(user.uid, player.id, amount, pin);
      setSuccess(
        `Successfully withdrew ${amount} points! New balance: ${result.newBalance} points`
      );

      // Update parent component
      if (onPointsUpdated) {
        onPointsUpdated();
      }

      // Clear form
      setWithdrawalAmount('');
      setPin('');

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setError(error.message || 'Failed to withdraw points');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setWithdrawalAmount(value);
    setError('');
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
    setError('');
  };

  const isFormValid =
    withdrawalAmount &&
    pin.length === 4 &&
    parseInt(withdrawalAmount) > 0 &&
    parseInt(withdrawalAmount) <= player.totalScore;

  return (
    <div className='point-bank-overlay'>
      <div className='point-bank-modal'>
        <div className='modal-header'>
          <h2>
            <span style={{ fontSize: 48 }}>{player.avatar || '🐶'}</span>{' '}
            {player.playerName}
            {player.playerName && !player.playerName.endsWith('s') ? "'s" : "'"}
            &nbsp;Bank
          </h2>
          <button
            className='close-btn'
            onClick={onClose}
            aria-label='Close modal'
          >
            <X size={20} />
          </button>
        </div>

        <div className='modal-content'>
          <div className='current-balance'>
            <div className='balance-label'>Current Balance</div>
            <div className='balance-amount'>
              {player.totalScore || 0} points
            </div>
          </div>

          {success ? (
            <div className='success-message'>{success}</div>
          ) : (
            <form onSubmit={handleWithdrawal}>
              <div className='input-group'>
                <label>Withdraw Amount (points):</label>
                <Input
                  type='text'
                  value={withdrawalAmount}
                  onChange={handleAmountChange}
                  placeholder='Enter amount'
                  disabled={isWithdrawing}
                  required
                />
              </div>

              <div className='input-group'>
                <label>Parent PIN:</label>
                <div className='pin-input-section'>
                  <Input
                    type='tel'
                    value={pin}
                    onChange={handlePinChange}
                    placeholder='0000'
                    maxLength={4}
                    pattern='[0-9]*'
                    inputMode='numeric'
                    autoComplete='off'
                    autoCorrect='off'
                    autoCapitalize='off'
                    spellCheck='false'
                    disabled={isWithdrawing}
                    required
                  />
                </div>
              </div>

              {error && <div className='error-message'>{error}</div>}

              <div className='modal-actions'>
                <Button
                  type='secondary'
                  onClick={onClose}
                  disabled={isWithdrawing}
                >
                  Cancel
                </Button>
                <Button type='primary' disabled={!isFormValid || isWithdrawing}>
                  {isWithdrawing ? 'Withdrawing...' : 'Withdraw Points'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default PointBankModal;
